/**
 * Centralized error reporting utility for client-side errors.
 * Sends errors to /api/jserrors with context and deduplication.
 */

// Track recently reported errors to avoid duplicates
const recentErrors = new Set();
const ERROR_DEDUPE_WINDOW = 5000; // 5 seconds
const MAX_ERRORS_PER_SESSION = 50;
let errorsReportedThisSession = 0;

/**
 * Reports an error to the server
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred (component name, function name, etc.)
 * @param {Object} additionalData - Any additional context to include
 * @returns {Promise<boolean>} - True if error was reported, false if deduplicated/rate-limited
 */
export async function reportError(error, context = 'unknown', additionalData = {}) {
    // Check rate limit
    if (errorsReportedThisSession >= MAX_ERRORS_PER_SESSION) {
        console.warn('[ErrorReporter] Rate limit reached, not reporting error');
        return false;
    }

    // Create error signature for deduplication
    const errorSignature = createErrorSignature(error, context);

    // Check if we recently reported this error
    if (recentErrors.has(errorSignature)) {
        console.log('[ErrorReporter] Skipping duplicate error:', errorSignature);
        return false;
    }

    // Mark error as reported
    recentErrors.add(errorSignature);
    setTimeout(() => recentErrors.delete(errorSignature), ERROR_DEDUPE_WINDOW);
    errorsReportedThisSession++;

    // Build error data
    const errorData = {
        message: sanitizeErrorMessage(error?.message || String(error)),
        stack: error?.stack || new Error().stack,
        context: context,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...additionalData
    };

    // Log to console for debugging
    console.error('[ErrorReporter] Reporting error:', errorData);

    // Send to server
    try {
        const response = await fetch('/api/jserrors', {
            method: 'POST',
            credentials: 'same-origin',
            headers: new Headers({ 'content-type': 'application/json' }),
            body: JSON.stringify(errorData)
        });

        if (!response.ok) {
            console.error('[ErrorReporter] Failed to report error to server:', response.status);
            return false;
        }

        return true;
    } catch (reportingError) {
        // Don't let error reporting errors break the app
        console.error('[ErrorReporter] Exception while reporting error:', reportingError);
        return false;
    }
}

/**
 * Creates a unique signature for an error to enable deduplication
 */
function createErrorSignature(error, context) {
    const message = error?.message || String(error);
    const stack = error?.stack || '';
    // Use first line of stack trace for signature (most specific location)
    const stackLine = stack.split('\n')[1] || '';
    return `${context}::${message}::${stackLine}`;
}

/**
 * Sanitizes error messages to remove potential PII
 */
function sanitizeErrorMessage(message) {
    // Remove potential email addresses
    message = message.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]');
    // Remove potential phone numbers
    message = message.replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[PHONE]');
    // Remove potential UUIDs (case IDs might be sensitive)
    message = message.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[UUID]');
    return message;
}

/**
 * Wraps an async function with error reporting
 * Usage: const safeFunction = withErrorReporting(myAsyncFunction, 'ComponentName.functionName');
 */
export function withErrorReporting(fn, context) {
    return async function (...args) {
        try {
            return await fn.apply(this, args);
        } catch (error) {
            await reportError(error, context, { args: sanitizeArgs(args) });
            throw error; // Re-throw so caller can handle it
        }
    };
}

/**
 * Sanitizes function arguments for logging
 */
function sanitizeArgs(args) {
    try {
        // Simple sanitization - just get types and length
        return args.map((arg, i) => ({
            index: i,
            type: typeof arg,
            isArray: Array.isArray(arg),
            length: arg?.length
        }));
    } catch (e) {
        return 'Could not sanitize args';
    }
}

/**
 * Resets the session error counter (useful for testing or long-running sessions)
 */
export function resetErrorCounter() {
    errorsReportedThisSession = 0;
}

